import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { ArrowLeftIcon, CheckCircleIcon, BanknotesIcon, PlusIcon, TrashIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import api from '../../api/client';

// Direct Deposit Authorization — inline fillable form.
// Replaces the "upload a paper form" path for the Phase 2 onboarding
// step. Submit generates a PDF, stamps the step complete, sends the
// candidate back to the onboarding hub.

const emptyAccount = () => ({
  account_holder: '',
  routing_number: '',
  account_number: '',
  account_type: 'checking',
  amount: '',
  entire_paycheck: true,
});

export default function MyDirectDepositPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ctx, setCtx] = useState(null);
  const [accounts, setAccounts] = useState([emptyAccount()]);
  const [signature, setSignature] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get('/my/direct-deposit')
      .then(({ data }) => setCtx(data))
      .catch(() => toast.error('Could not load your profile.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="text-gray-400">Loading…</div></div>;
  }
  if (!ctx) return null;

  const fullName = `${ctx.first_name} ${ctx.last_name}`.trim();
  const alreadyDone = ctx.step_status === 'completed' || ctx.step_status === 'verified';

  const updateAccount = (idx, patch) => {
    setAccounts(prev => prev.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
  };

  const addAccount = () => {
    if (accounts.length >= 3) return;
    setAccounts(prev => [...prev, { ...emptyAccount(), entire_paycheck: false }]);
  };

  const removeAccount = (idx) => {
    setAccounts(prev => prev.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    if (!signature.trim()) return toast.error('Please type your signature.');
    if (signature.trim().toLowerCase() !== fullName.toLowerCase()) {
      return toast.error(`Signature must match your name exactly: "${fullName}".`);
    }
    setSubmitting(true);
    try {
      const { data } = await api.post('/my/direct-deposit', {
        accounts: accounts.map(a => ({
          account_holder: a.account_holder,
          routing_number: a.routing_number,
          account_number: a.account_number,
          account_type: a.account_type,
          entire_paycheck: !!a.entire_paycheck,
          amount: a.entire_paycheck ? null : a.amount,
        })),
        signature: signature.trim(),
      });
      toast.success('Direct Deposit submitted!');
      // Soft delay so the candidate sees the toast before /my reroutes.
      setTimeout(() => navigate('/my/onboarding'), 800);
      return data;
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not submit Direct Deposit.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <Toaster position="top-right" />
      <div className="max-w-2xl mx-auto">
        <Link to="/my/onboarding" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
          <ArrowLeftIcon className="w-4 h-4" /> Back to onboarding
        </Link>

        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="bg-orange-50 border-b border-orange-100 p-5 flex items-center gap-3">
            <BanknotesIcon className="w-6 h-6 text-orange-500" />
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Direct Deposit Authorization</h1>
              <p className="text-xs text-gray-500 mt-0.5">Up to 3 bank accounts. Your information is encrypted in transit and at rest.</p>
            </div>
          </div>

          {alreadyDone ? (
            <div className="p-8 space-y-4">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircleIcon className="w-6 h-6" />
                <p className="font-medium">Direct Deposit already submitted</p>
              </div>
              <p className="text-sm text-gray-600">
                You sent in your Direct Deposit form on{' '}
                {ctx.completed_at ? new Date(ctx.completed_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'an earlier date'}.
                If you need to change it, contact your admin to reset this step before re-submitting.
              </p>
              {ctx.document_url && (
                <a href={ctx.document_url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-orange-600 hover:underline">
                  <DocumentTextIcon className="w-4 h-4" /> View submitted PDF
                </a>
              )}
            </div>
          ) : (
            <div className="p-6 space-y-5">
              {accounts.map((acct, i) => (
                <div key={i} className="rounded-xl border border-gray-200 p-4 space-y-3 bg-gray-50/40">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-orange-600">Account {i + 1}</h3>
                    {accounts.length > 1 && (
                      <button onClick={() => removeAccount(i)} className="text-gray-400 hover:text-red-500 inline-flex items-center gap-1 text-xs">
                        <TrashIcon className="w-3.5 h-3.5" /> Remove
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Name on bank account</label>
                      <input value={acct.account_holder}
                        onChange={(e) => updateAccount(i, { account_holder: e.target.value })}
                        className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Routing number (9 digits)</label>
                        <input value={acct.routing_number}
                          inputMode="numeric"
                          maxLength={12}
                          onChange={(e) => updateAccount(i, { routing_number: e.target.value.replace(/\D/g, '') })}
                          className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Account number</label>
                        <input value={acct.account_number}
                          inputMode="numeric"
                          maxLength={20}
                          onChange={(e) => updateAccount(i, { account_number: e.target.value.replace(/\D/g, '') })}
                          className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Account type</label>
                        <select value={acct.account_type}
                          onChange={(e) => updateAccount(i, { account_type: e.target.value })}
                          className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500">
                          <option value="checking">Checking</option>
                          <option value="savings">Savings</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Amount</label>
                        <div className="flex items-center gap-2">
                          <input type="checkbox" id={`entire-${i}`}
                            checked={!!acct.entire_paycheck}
                            onChange={(e) => updateAccount(i, { entire_paycheck: e.target.checked, amount: e.target.checked ? '' : acct.amount })}
                            className="rounded" />
                          <label htmlFor={`entire-${i}`} className="text-xs text-gray-700 whitespace-nowrap">Entire paycheck</label>
                          <input value={acct.amount}
                            placeholder="$ amount"
                            disabled={!!acct.entire_paycheck}
                            inputMode="decimal"
                            onChange={(e) => updateAccount(i, { amount: e.target.value })}
                            className="flex-1 bg-white border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-orange-500 disabled:bg-gray-100 disabled:text-gray-400" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {accounts.length < 3 && (
                <button onClick={addAccount}
                  className="w-full bg-orange-50 border border-dashed border-orange-200 text-orange-600 px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-orange-100 inline-flex items-center justify-center gap-1">
                  <PlusIcon className="w-4 h-4" /> Add another account
                </button>
              )}

              <div className="border-t border-gray-100 pt-4">
                <label className="block text-xs font-medium text-gray-700 mb-1">Signature</label>
                <p className="text-[11px] text-gray-500 mb-2">
                  Type your full name exactly as <strong>{fullName}</strong> to authorize this Direct Deposit.
                </p>
                <input value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  placeholder={fullName}
                  className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2.5 text-sm font-medium focus:outline-none focus:border-orange-500" />
              </div>

              <button onClick={submit} disabled={submitting}
                className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/40 text-white font-semibold py-3 rounded-xl">
                {submitting ? 'Submitting…' : 'Submit Direct Deposit'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
